import type { BuiltInRoleCode } from "../domain/identity/access-types.js";
import { BUILT_IN_ROLE_PERMISSIONS } from "./built-in-role-policy.js";

/**
 * Single source of truth for computing a user's effective permission set:
 * Role permissions (BUILT_IN_ROLE_PERMISSIONS) UNIONED with any additional
 * permissions explicitly granted to that user (`customPermissions`).
 *
 * `customPermissions` are ADDITIONS on top of the role, never a replacement
 * of it — a user assigned the ENGINEER role always keeps every permission
 * ENGINEER grants, regardless of what has been individually customized for
 * them. Overriding (denying) a specific permission the role would otherwise
 * grant is not supported by this model (`customPermissions` is a flat list
 * of extra grants, not a {granted, denied} structure) — see
 * docs/RBAC.md for that documented limitation and proposal.
 */
/** Permissão do Córtex descontinuado (migration 002). Não é concedida por perfil. */
export const LEGACY_CORTEX_USE = "cortex.use";
/** O que o 'cortex.use' permitia (usar as capacidades de IA) no INSPECT IA REVIEW. */
export const CORTEX_USE_EQUIVALENTS: readonly string[] = ["ai_review.read", "ai_review.execute", "ai_assistant.use"];

const ROLE_ALIASES: Record<string, BuiltInRoleCode> = {
  ADMINISTRATOR: "ADMIN" as BuiltInRoleCode,
  ADMIN: "ADMIN" as BuiltInRoleCode,
  ENGINEER: "ENGINEER" as BuiltInRoleCode,
  ENGENHEIRO: "ENGINEER" as BuiltInRoleCode,
  TECHNICIAN: "TECHNICIAN" as BuiltInRoleCode,
  TECNICO: "TECHNICIAN" as BuiltInRoleCode,
  TÉCNICO: "TECHNICIAN" as BuiltInRoleCode,
  INSPECTOR: "INSPECTOR" as BuiltInRoleCode,
  INSPETOR: "INSPECTOR" as BuiltInRoleCode,
  DOCUMENTATION: "DOCUMENTATION" as BuiltInRoleCode,
  DOCUMENTACAO: "DOCUMENTATION" as BuiltInRoleCode,
  DOCUMENTAÇÃO: "DOCUMENTATION" as BuiltInRoleCode,
  MANAGER: "MANAGER" as BuiltInRoleCode,
  GERENTE: "MANAGER" as BuiltInRoleCode,
  DEVELOPER: "DEVELOPER" as BuiltInRoleCode,
  DESENVOLVEDOR: "DEVELOPER" as BuiltInRoleCode,
  VIEWER: "VIEWER" as BuiltInRoleCode,
  VISUALIZADOR: "VIEWER" as BuiltInRoleCode
};

export function normalizeRoleCode(roleCode?: string | null): BuiltInRoleCode {
  if (!roleCode || typeof roleCode !== "string") return "VIEWER" as BuiltInRoleCode;
  const clean = roleCode.trim().toUpperCase();
  return ROLE_ALIASES[clean] || (clean as BuiltInRoleCode);
}

export function resolveEffectivePermissions(
  roleCode: string,
  customPermissions?: readonly string[] | undefined
): Set<string> {
  const normalizedRole = normalizeRoleCode(roleCode);
  const base = BUILT_IN_ROLE_PERMISSIONS[normalizedRole] ?? [];
  const effective = new Set<string>(base);
  if (customPermissions) {
    for (const permission of customPermissions) {
      effective.add(permission);
      // LEGADO DO CÓRTEX: quem tinha 'cortex.use' como permissão individual
      // continua com o equivalente no INSPECT IA REVIEW — a migração não tira
      // acesso de ninguém, e nada precisa ser regravado (recalculado a cada sessão).
      if (permission === LEGACY_CORTEX_USE) for (const p of CORTEX_USE_EQUIVALENTS) effective.add(p);
    }
  }
  return effective;
}
